import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { xcss, Box } from '@atlaskit/primitives';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	FieldWrapper,
	SideBySideField,
	FieldHeadingTitle,
	FieldHeadingSideBySide,
} from '@atlassian/jira-issue-field-heading/src/styled.tsx';
import { FieldPin } from '@atlassian/jira-issue-field-pin/src/index.tsx';
import { useUserPreferredWorkItemLayout } from '@atlassian/jira-issue-view-layout/src/services/main.tsx';
import { DEV_SUMMARY_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const DevelopmentFieldWrapper = ({
	children,
	afterHeadingNode,
	pinnable = false,
}: Props) => {
	const { formatMessage } = useIntl();
	const titleLabel = formatMessage(messages.label);
	return (
		<FieldWrapper>
			<DevPanelFieldHeading>
				<FieldHeadingTitle>{titleLabel}</FieldHeadingTitle>
				{afterHeadingNode}
				{pinnable && <FieldPin fieldId={DEV_SUMMARY_TYPE} label={titleLabel} />}
			</DevPanelFieldHeading>
			<SideBySideField>{children}</SideBySideField>
		</FieldWrapper>
	);
};

export default DevelopmentFieldWrapper;

const DevPanelFieldHeadingNew = ({ children }: { children: ReactNode }) => {
	const { userPreferredLayout } = useUserPreferredWorkItemLayout();
	const isWideViewLayout = userPreferredLayout === 'WIDE';
	return (
		<Box xcss={[fieldHeadingSideBySideCssStylesNew, isWideViewLayout && wideLayoutStyles]}>
			<Box xcss={devPanelFieldHeadingOuterStyles}>{children}</Box>
		</Box>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DevPanelFieldHeadingOld = styled(FieldHeadingSideBySide)({
	display: 'flex',
	paddingRight: '0px',
});

const fieldHeadingSideBySideCssStylesNew = xcss({
	position: 'relative',
	boxSizing: 'border-box',
	minWidth: '120px',
	width: '40%',
	flex: '1 0 0%',
	display: 'flex',
	paddingRight: 'space.200',
	paddingTop: 'space.100',
});

const wideLayoutStyles = xcss({
	maxWidth: '148px',
	paddingRight: 'space.0',
});

const devPanelFieldHeadingOuterStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	height: 'fit-content',
	paddingBottom: 'space.100',
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
	borderRadius: '4px',
	width: 'fit-content',
	marginLeft: 'space.negative.050',
	gap: 'space.100',
	':hover': {
		backgroundColor: 'color.background.neutral.hovered',
	},
});

const DevPanelFieldHeading = componentWithFG(
	'issue_view_field_config_edit',
	DevPanelFieldHeadingNew,
	DevPanelFieldHeadingOld,
);
